import { useMemo } from 'react';
import Highcharts, { TooltipFormatterContextObject } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { Metrics } from 'components/insights/constants';
import { formatPercentage, formatResponseTime } from 'components/insights/formatting';
import AnalyticsResponseData from 'components/insights/query/AnalyticsResponseData';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { useInboundReferralsInsightsStore } from 'stores/inboundReferralsInsightsStore';
import { colors } from 'styles/theme/colors';
import textStyles from 'styles/theme/textStyles';

import { type ScrollConfig } from '../../networkPerformance/slideout/headers/chartHelpers';
import MetricDetailLayout from '../../shared/slideout/MetricDetailLayout';
import { StatTile } from '../../shared/slideout/statTiles';
import { INBOUND_DIMENSION_LABELS } from '../constants';

// Registers the shared `column2` overlaid-column series type used by the chart below.
import 'components/shared/charts/column2Series';

const POINT_WIDTH = 40;

type Props = {
  loading: boolean;
  rollups: AnalyticsResponseRollupCollection;
  data: AnalyticsResponseData;
};

// Metric drill for the Accepted rate tile: acceptance rate + avg decision time, over a stacked
// "received vs accepted" bar per View-By entity.
export default function AcceptedDetailHeader({ loading, rollups, data }: Props) {
  const slideoutDimension = useInboundReferralsInsightsStore((state) => state.slideoutDimension);

  const overall = rollups.overall.metrics;
  const acceptanceRate = overall.getCurrentDataPoint(Metrics.ACCEPTANCE_RATE, 0);
  const acceptedCount = overall.getCurrentDataPoint(Metrics.ACCEPTED_COUNT, 0) ?? 0;
  const totalReceived = overall.getCurrentDataPoint(Metrics.REFERRAL_COUNT, 0) ?? 0;
  const avgTimeToAcceptedSeconds = overall.getCurrentDataPoint(Metrics.TIME_TO_ACCEPTED_DECISION_SECONDS_AVERAGE, 0);

  const entityLabel = slideoutDimension ? (INBOUND_DIMENSION_LABELS[slideoutDimension] ?? '') : '';

  const { chartOptions, scrollConfig, isEmpty } = useMemo(() => {
    const names = data.dimensions.getCurrentDimensionValuesByIndex(0);
    const received = data.metrics.getCurrentSeries(Metrics.REFERRAL_COUNT);
    const accepted = data.metrics.getCurrentSeries(Metrics.ACCEPTED_COUNT);
    const rates = data.metrics.getCurrentSeries(Metrics.ACCEPTANCE_RATE);

    // Sort by acceptance rate descending — matches the breakdown table's default sort (same pattern
    // as network performance's ReadmissionRateHeader).
    const order = names.map((_, i) => i).sort((a, b) => (rates[b] ?? 0) - (rates[a] ?? 0));
    const sortedNames = order.map((i) => names[i] || 'None');
    const sortedReceived = order.map((i) => received[i] ?? 0);
    const sortedAccepted = order.map((i) => accepted[i] ?? 0);
    const sortedRates = order.map((i) => rates[i] ?? null);

    const options: Highcharts.Options = {
      chart: { type: 'column', style: { fontFamily: textStyles.base.fontFamily }, height: 280 },
      accessibility: { enabled: false },
      credits: { enabled: false },
      title: { text: undefined },
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        symbolRadius: 6,
        itemStyle: { fontSize: '12px', fontWeight: 'normal', color: colors.black75 },
      },
      xAxis: {
        categories: sortedNames,
        lineColor: colors.black10,
        tickLength: 0,
        labels: { style: { fontSize: '11px', color: colors.black75 } },
      },
      yAxis: {
        title: { text: undefined },
        gridLineColor: colors.black10,
        labels: { style: { fontSize: '11px', color: colors.black50 } },
      },
      tooltip: { enabled: false },
      plotOptions: {
        column: {
          borderRadius: { radius: 3, scope: 'point' as const, where: 'end' as const },
          pointWidth: POINT_WIDTH,
        },
        // @ts-expect-error column2 is our custom side-by-side series type, not on the plotOptions type
        column2: {
          borderRadius: { radius: 3, scope: 'point' as const, where: 'end' as const },
          pointWidth: POINT_WIDTH,
        },
        series: { animation: false, inactiveOtherPoints: true, events: { legendItemClick: () => false } },
      },
      series: [
        {
          type: 'column',
          name: 'Total Received',
          color: colors.black10,
          data: sortedReceived.map((y, i) => ({
            y,
            label: formatPercentage(sortedRates[i]),
            dataLabels: {
              enabled: true,
              formatter: function (this: TooltipFormatterContextObject): string {
                return `<span style="font-size:10px;font-weight:normal;">${this.point.label}</span>`;
              },
            },
          })),
        },
        { type: 'column2' as 'column', name: 'Total Accepted', color: colors.green100, data: sortedAccepted },
      ],
    };

    const scroll: ScrollConfig = { minWidth: (POINT_WIDTH + POINT_WIDTH * 1.5) * sortedNames.length };
    return { chartOptions: options, scrollConfig: scroll, isEmpty: sortedNames.length === 0 };
  }, [data]);

  return (
    <MetricDetailLayout
      tiles={
        <>
          <StatTile
            label='Avg. Acceptance Rate'
            tone='green'
            loading={loading}
            inlineAddendum
            primary={formatPercentage(acceptanceRate)}
            addendum={acceptanceRate != null ? `${acceptedCount} of ${totalReceived} referrals received` : undefined}
          />
          <StatTile
            label='Avg. Time to Acceptance'
            loading={loading}
            primary={formatResponseTime(avgTimeToAcceptedSeconds)}
          />
        </>
      }
      chartTitle={`Acceptance Rate by ${entityLabel}`}
      chartLoading={loading}
      isEmpty={isEmpty}
      scrollConfig={scrollConfig}
      emptyMessage='No accepted referrals to show for this time period.'>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </MetricDetailLayout>
  );
}
